<script setup lang="ts">
import type { StyleValue } from 'vue';
import { computed, useAttrs } from 'vue';

export interface Props {
  size?: number;
  iconColorClass?: string;
  textColorClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 60,
  iconColorClass: '',
  textColorClass: '',
});

const attrs = useAttrs();

const VIEWBOX_WIDTH = 284;
const VIEWBOX_HEIGHT = 70;

const svgHeight = computed(() => Math.max(1, Math.round(props.size)));
const svgWidth = computed(() =>
  Math.max(1, Math.round((svgHeight.value * VIEWBOX_WIDTH) / VIEWBOX_HEIGHT)),
);

const rootStyle = computed(() => ({
  width: `${svgWidth.value}px`,
  height: `${svgHeight.value}px`,
}));

const mergedStyle = computed<StyleValue>(() => {
  const extraStyle = attrs.style as StyleValue | undefined;
  return extraStyle ? [rootStyle.value, extraStyle] : rootStyle.value;
});

const passthroughAttrs = computed(() => {
  const rest = { ...(attrs as Record<string, unknown>) };
  delete rest.class;
  delete rest.style;
  return rest;
});

const rootClass = computed(() => attrs.class as unknown);
</script>

<template>
  <svg
    class="inline-block"
    :class="rootClass"
    :style="mergedStyle"
    :width="svgWidth"
    :height="svgHeight"
    viewBox="0 0 284 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    v-bind="passthroughAttrs"
  >
    <g data-part="word" :class="props.textColorClass">
      <path
        d="M88.5902 39.935H96.8424C96.8424 45.4786 101.401 48.5425 107.211 48.5425C112.484 48.5425 116.992 45.7721 116.992 41.2103C116.992 36.2753 111.687 35.0311 105.712 33.6246C98.1279 31.7823 89.4291 29.6865 89.4291 19.4514C89.4291 10.5007 96.2053 5.21642 106.765 5.21642C117.518 5.21642 123.88 11.0782 123.88 20.4335H115.839C115.839 15.4861 111.756 12.8064 106.583 12.8064C101.643 12.8064 97.5603 15.0713 97.5603 19.1063C97.5603 23.6872 102.654 24.9321 108.537 26.3379C116.243 28.251 125.316 30.4587 125.316 41.1186C125.316 51.0423 117.314 56.3156 107.281 56.3156C96.1658 56.3156 88.5902 50.0696 88.5902 39.935Z"
        fill="currentColor"
      />
      <path
        d="M164.222 36.4235C164.222 29.2109 158.956 23.8699 151.75 23.8699C144.545 23.8699 139.229 29.2614 139.229 36.4235C139.229 43.5856 144.545 48.9767 151.75 48.9767C158.956 48.9767 164.222 43.6356 164.222 36.4235ZM152.368 16.3606C163.807 16.3606 172.171 24.9046 172.171 36.4242C172.171 47.893 163.807 56.5074 152.368 56.5074C147.419 56.5074 143.025 54.7919 139.676 51.8734V69.2713H131.818V17.293H137.113L138.59 21.9917C142.047 18.465 146.864 16.3613 152.368 16.3613V16.3606Z"
        fill="currentColor"
      />
      <path
        d="M208.431 36.4042C208.431 29.192 203.166 23.8509 195.96 23.8509C188.755 23.8509 183.439 29.2421 183.439 36.4042C183.439 43.5663 188.755 48.9578 195.96 48.9578C203.166 48.9578 208.431 43.6164 208.431 36.4042ZM216.308 17.2734V55.5349H209.97L209.205 50.8822C205.743 54.3884 200.913 56.4878 195.342 56.4878C183.955 56.4878 175.439 47.873 175.439 36.4042C175.439 24.905 183.955 16.3614 195.342 16.3614C200.978 16.3614 205.855 18.4919 209.325 22.0574L210.244 17.2727L216.308 17.2734Z"
        fill="currentColor"
      />
      <path
        d="M246.288 17.0804V24.5099H242.139C235.64 24.5099 232.397 28.2198 232.397 35.1508V55.5551H224.539V17.2932H229.854L231.184 22.516C233.919 18.8867 237.669 17.0804 242.89 17.0804H246.288Z"
        fill="currentColor"
      />
      <path
        d="M262.72 39.2326H257.957V55.5553H250.099V3.5777H257.957V32.0888H262.776L273.094 17.293H282.408L269.465 35.6579L283.784 55.5553H274.175L262.72 39.2326Z"
        fill="currentColor"
      />
    </g>

    <g data-part="mark" :class="props.iconColorClass">
      <path d="M0 33.8952H26.0477V26.2978H0V33.8952Z" fill="currentColor" />
      <path
        d="M65.2893 26.2978H39.2416V33.8952H65.2893V26.2978Z"
        fill="currentColor"
      />
      <path
        d="M19.6207 2.14769e-06L13.0238 3.79871L26.0477 26.2978L32.6446 22.4991L19.6207 2.14769e-06Z"
        fill="currentColor"
      />
      <path
        d="M45.6685 60.1929L52.2654 56.3942L39.2416 33.8951L32.6447 37.6938L45.6685 60.1929Z"
        fill="currentColor"
      />
      <path
        d="M52.2656 3.79875L45.6686 4.02947e-05L32.6448 22.4991L39.2417 26.2979L52.2656 3.79875Z"
        fill="currentColor"
      />
      <path
        d="M13.0239 56.3943L19.6209 60.193L32.6447 37.6936L26.0478 33.8952L13.0239 56.3943Z"
        fill="currentColor"
      />
    </g>
  </svg>
</template>
